import React, { ReactElement, useEffect } from 'react';
import { useLocation, useParams } from 'react-router';
import { Loader } from 'idsk-react-components';
import {
  Breadcrumbs, getTitle, homeBreadcrumb, setTitle,
} from 'slovlex-idsk-react-components';
import PageHeadingWithBackButton from '../../module/PageHeadingWithBackButton';
import routes from '../../routing/routes';

import GlobalErrors from '../../module/GlobalErrors';
import Layout from '../Layout';
import SecuredPage from '../../module/SecuredPage';
import PracovnyKrokEnum from '../../model/pracovnyKrok/PracovnyKrokEnum';
import SprievodnyDokumentZmenaPoradia
  from '../../module/SprievodnyDokument/SprievodnyDokumentZmenaPoradia';
import {
  useLegislativnyMaterialDetail,
} from '../../module/LegislativnyMaterial/LegislativnyMaterialDetail';
import useLegislativnyMaterialHistoria
  from '../../module/LegislativnyMaterial/LegislativnyMaterialHistoria/hooks/useLegislativnyMaterialHistoria';
import { pouzivatelIsAdmin, pouzivatelIsPrihlaseny } from '../../function/permissions';
import { useUserContext } from '../../context/UserContext';
import SuccessMessages from '../../module/SuccessMessages';
import isPredbeznaInformacia from '../../function/isPredbeznaInformacia';

const SprievodnyDokumentZmenaPoradiaPage = (): ReactElement => {
  const {
    materialDetail,
    materialStatus,
  } = useLegislativnyMaterialDetail(PracovnyKrokEnum.SPRIEVODNY_DOKUMENT_FUNCTION);
  const {
    type,
    year,
    index,
    // @ts-ignore
  } = useParams<{ type: string, year: number, index: number}>();
  const { pouzivatel } = useUserContext();
  const location = useLocation();
  const stadiumUuidSearchParam = new URLSearchParams(location.search).get('stadiumUuid');

  const {
    legMaterialHistoriaList,
    legMaterialHistoriaStatus,
  } = useLegislativnyMaterialHistoria(materialDetail?.uuid);

  useEffect(() => {
    if (!legMaterialHistoriaStatus.loading && !materialStatus.loading) {
      setTitle(getTitle());
    }
  }, [legMaterialHistoriaStatus.loading, materialStatus.loading]);

  return (
    <Layout>
      <>
        <Breadcrumbs routeList={[
          homeBreadcrumb(process.env.REACT_APP_PORTAL_MAIN_URL),
          {
            heading: isPredbeznaInformacia(type) ? 'Predbežné informácie' : 'Legislatívne procesy',
            to: isPredbeznaInformacia(type) ? routes.procesy.predbeznaInformacia.list : routes.procesy.list,
          },
          {
            heading: `${type}/${year}/${index}`,
            to: routes.procesy.detail.withParams(type, year, index),
          },
          {
            heading: 'Sprievodná dokumentácia',
            to: routes.procesy.detail.sprievodneDokumenty.editAsAdmin.withParams(
              type,
              year,
              index,
              stadiumUuidSearchParam,
            ),
          },
          {
            heading: 'Zmena poradia sprievodných dokumentov',
            to: routes.procesy.detail.sprievodneDokumenty.editOrder.withParams(
              type,
              year,
              index,
              stadiumUuidSearchParam,
            ),
          },
        ]}
        />
        <GlobalErrors />
        <Loader loading={materialStatus.loading || legMaterialHistoriaStatus.loading}>
          <SecuredPage
            additionalCheck={pouzivatelIsPrihlaseny(pouzivatel)
                            && (pouzivatelIsAdmin(pouzivatel)
                                || materialDetail?.hasOpravnenieSprievodneDokumenty)}
            requestStatus={materialStatus.responseCode}
          >
            <>
              <SuccessMessages />
              {legMaterialHistoriaList && materialDetail && (
              <div className="sprievodny-dokument-zmena-poradia-list-page">
                <PageHeadingWithBackButton
                  headingCaption={`Legislatívny proces ${materialDetail.cisloLegislativnehoMaterialu}`}
                  heading="Zmena poradia sprievodných dokumentov"
                  backLinkText={`Späť na ${materialDetail.cisloLegislativnehoMaterialu}`}
                  backLinkRoute={routes.procesy.detail.withParams(type, year, index)}
                />
                <SprievodnyDokumentZmenaPoradia
                  materialDetail={materialDetail}
                  legMaterialHistoriaList={legMaterialHistoriaList}
                />
              </div>
              )}
            </>
          </SecuredPage>
        </Loader>
      </>
    </Layout>
  );
};

export default SprievodnyDokumentZmenaPoradiaPage;
